import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { screenPath } from '@/lib/engagedTime';
import { createFigureTracker, type ExportKind, type FigureBatch, type Surface } from '@/lib/figureAttention';

/* SPEC-545 — figure attention. `useFigureAttention()` is mounted ONCE (AuthGate, beside
 * useEngagedTime); it owns the tracker (rules in lib/figureAttention.ts), one IntersectionObserver
 * and the copy/print listeners, and posts at most one batch per 30 s to POST /api/analytics/figures
 * (keepalive on hide/close — sendBeacon cannot carry the Authorization header).
 *
 * A screen opts in with `useFigureSurface(surface)`: put the returned ref on the container and a
 * `data-figure-id="<product code>"` on each figure row. A row counts once at least half of it is on
 * screen. Time on screen is recorded — never what the figure says, never "used" (NK-186). */

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'] as const;
const VISIBLE_RATIO = 0.5;

let tracker: ReturnType<typeof createFigureTracker> | null = null;
let observer: IntersectionObserver | null = null;
const meta = new WeakMap<Element, { s: Surface; id: string }>();

/* Created on first use, not in the app hook: React runs a child's effects BEFORE its parent's, so
 * a screen's rows are registered before useFigureAttention's own effect has run. The callback
 * reads the tracker when it fires, by which time it exists. Lives for the page's lifetime. */
function obs(): IntersectionObserver | null {
  if (!observer && typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const m = meta.get(e.target);
        // Half the row on screen — or, for a panel taller than the window, half the window filled by it.
        const fills = !!e.rootBounds && e.intersectionRect.height >= e.rootBounds.height * VISIBLE_RATIO;
        if (m) tracker?.setOnScreen(m.s, m.id, e.isIntersecting && (e.intersectionRatio >= VISIBLE_RATIO || fills));
      }
    }, { threshold: [0, 0.1, 0.25, VISIBLE_RATIO] });
  }
  return observer;
}

function post(batch: FigureBatch, final: boolean) {
  const token = localStorage.getItem('suryaiq_token');
  if (!token) return;
  fetch('/api/analytics/figures', {
    method: 'POST',
    keepalive: final,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(batch),
  }).catch(() => {});
}

/** A CSV download (or another export) of figures on a surface. Rides the next batch. */
export function recordFigureExport(kind: ExportKind, surface: Surface, ids: string[]) {
  tracker?.exported(kind, surface, ids);
}

function figureOf(node: Node | null): { s: Surface; id: string | null } | null {
  const el = node instanceof Element ? node : node?.parentElement ?? null;
  const root = el?.closest('[data-figure-surface]');
  if (!root) return null;
  const row = el?.closest('[data-figure-id]');
  return { s: root.getAttribute('data-figure-surface') as Surface, id: row ? row.getAttribute('data-figure-id') : null };
}

export function useFigureAttention() {
  const location = useLocation();

  useEffect(() => {
    tracker = createFigureTracker(
      { now: () => Date.now(), visible: () => document.visibilityState === 'visible', focused: () => document.hasFocus() },
      post,
    );
    const onInput = () => tracker?.input();
    const onVisibility = () => { if (document.visibilityState !== 'visible') tracker?.hide(); };
    const onHide = () => tracker?.hide();
    const onCopy = () => {
      const f = figureOf(document.getSelection()?.anchorNode ?? null);
      if (f) tracker?.exported('copy', f.s, f.id ? [f.id] : []);
    };
    const onPrint = () => {
      const ids = tracker?.onScreenIds() ?? [];
      if (ids.length) tracker?.exported('print', 'page', ids);
    };
    INPUT_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('copy', onCopy);
    window.addEventListener('beforeprint', onPrint);
    window.addEventListener('pagehide', onHide);
    const id = window.setInterval(() => tracker?.tick(), 1000);
    return () => {
      window.clearInterval(id);
      tracker?.hide();
      INPUT_EVENTS.forEach((e) => window.removeEventListener(e, onInput, { capture: true }));
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('copy', onCopy);
      window.removeEventListener('beforeprint', onPrint);
      window.removeEventListener('pagehide', onHide);
      tracker = null;
    };
  }, []);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    tracker?.setScreen({ path: screenPath(location.pathname), tab: tab ? tab.slice(0, 100) : null });
  }, [location.pathname, location.search]);
}

/** Opt a container in. Returns a callback ref for the container element. Rows are the
 * `[data-figure-id]` elements inside it that do not belong to a nested surface. */
export function useFigureSurface(surface: Surface) {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!root) return;
    root.setAttribute('data-figure-surface', surface);
    const seen = new Set<Element>();
    const drop = (el: Element) => {
      obs()?.unobserve(el);
      const m = meta.get(el);
      if (m) tracker?.setOnScreen(m.s, m.id, false);
      seen.delete(el);
    };
    const scan = () => {
      const now = new Set<Element>();
      root.querySelectorAll('[data-figure-id]').forEach((el) => {
        if (el.closest('[data-figure-surface]') === root) now.add(el);
      });
      now.forEach((el) => {
        const fid = el.getAttribute('data-figure-id');
        if (seen.has(el) || !fid) return;
        meta.set(el, { s: surface, id: fid });
        obs()?.observe(el);
        seen.add(el);
      });
      [...seen].forEach((el) => { if (!now.has(el)) drop(el); });
    };
    scan();
    let raf = 0;
    const mo = new MutationObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(scan); });
    // SPEC-547: a row gains or loses data-figure-id when its figures load or empty, on the same element.
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-figure-id'] });
    return () => {
      mo.disconnect();
      cancelAnimationFrame(raf);
      [...seen].forEach(drop);
    };
  }, [root, surface]);
  return setRoot;
}
